import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, useScroll } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFacebook, faInstagram, faLinkedin, faTiktok 
} from '@fortawesome/free-brands-svg-icons';
import { 
  faChevronDown, 
  faBars, faTimes, faGlobe 
} from '@fortawesome/free-solid-svg-icons';
import logo from '../images/Logo_no_bg.webp';
import './Header.css';
import { useCurrentDepartment } from '../hooks/useCurrentDepartment';

const Header = () => {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const menuRef = useRef(null);
  const location = useLocation();
  const currentDepartment = useCurrentDepartment();

  const { scrollY } = useScroll();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const unsubscribe = scrollY.on('change', (v) => {
      setIsScrolled(v > 50);
    });
    return () => unsubscribe();
  }, [scrollY]);

  useEffect(() => {
    closeMenu();
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveDropdown(null);
        if (window.innerWidth <= 992) {
          closeMenu();
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isMenuOpen]);

  const closeMenu = () => {
    setIsMenuOpen(false);
    setActiveDropdown(null);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (!isMenuOpen) {
      setActiveDropdown(null);
    }
  };

  const toggleDropdown = (dropdown) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
  };

  const handleLinkClick = () => {
    closeMenu();
  };

  const navItems = [
    { id: 'home', path: '/', label: t('menu_Accueil') },
    {
      id: 'services',
      path: '/services',
      label: t('Services'),
      dropdown: [
        { path: '/genie-informatique', label: t('Sous_Services_devLog') },
        { path: '/communication', label: t('Sous_Services_market') },
        { path: '/genie-electrique', label: t('Sous_Services_Elect') },
        { path: '/genie-civil', label: t('Sous_Services_BTP') },
      ]
    },
     {
      id: 'training',
      path: '/training',
      label: t('Formation'),
      dropdown: [
        { path: '/formation/informatique', label: t('Sous_Formation_informatiq') },
        { path: '/formation/electrique', label: t('Sous_Services_Elect') },
        { path: '/formation/civil', label: t('Sous_Services_BTP') },
        { path: '/formation/marketing', label: t('Sous_Services_market') },
      ]
    },
    /*{
      id: 'courses',
      path: '/courses',
      label: t('Cours_scolaire'),
      dropdown: [
        { path: '/courses/general', label: t('Cours_scolaire_general') },
        { path: '/courses/technical', label: t('Cours_scolaire_techniq') },
      ]
    }*/
    { id: 'about', path: '/apropos', label: t('Cours_scolaire_Apropos') },
  ];

  // La top bar : toujours visible dans le header normal, disparaît dans le header fixe après scroll
  const topBarAnimation = {
    height: "auto",
    paddingTop: 8,
    paddingBottom: 8,
    opacity: 1,
    marginBottom: 0,
};

  return (
    <>
      <div className="header-wrapper">
        {/* Top Bar */}
        <motion.div
          className="header-top"
          animate={topBarAnimation}
          transition={{ duration: 0.3 }}
          style={{ overflow: 'hidden' }}
        >
          {/* On peut conditionner l'affichage du contenu pour éviter de gaspiller du DOM, 
              mais ce n'est pas obligatoire */}
          <div className="container">
            <div className="top-bar-content">
              <div className="top-bar-right">
                <div className="social-icons">
                  <a href="https://www.facebook.com/profile.php?id=100080652765508" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                    <FontAwesomeIcon icon={faFacebook} />
                  </a>
                  <a href="https://www.instagram.com/atchhub237?igsi=ejB6c2tiZHl3MnAw" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                    <FontAwesomeIcon icon={faInstagram} />
                  </a>
                  <a href="https://www.linkedin.com/company/118613966/admin/dashboard/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                    <FontAwesomeIcon icon={faLinkedin} />
                  </a>
                  <a href="https://www.tiktok.com/@atc_hub_techpreneuriat?_r=1&_t=ZS-99QD1gHuP3d" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                    <FontAwesomeIcon icon={faTiktok} />
                  </a>
                </div>
                <div className="language-selector">
                  <FontAwesomeIcon icon={faGlobe} className="lang-icon" />
                  <select 
                    onChange={(e) => changeLanguage(e.target.value)} 
                    value={i18n.language}
                    aria-label={t('menu_langue')}
                  >
                    <option value="fr">🇫🇷 Français</option>
                    <option value="en">🇬🇧 English</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

      
        <header
          className={`main-header ${isScrolled ? 'sticky' : ''}`}
        >
          <div className="container">
            <div className="header-content">
              <div className="logo">
                <Link to="/" aria-label="ATC - Accueil">
                  <img src={logo} alt="ATC - Innovation & Ingénierie" />
                </Link>
              </div>

              <button 
                className={`mobile-menu-toggle ${isMenuOpen ? 'active' : ''}`} 
                onClick={toggleMenu}
                aria-label={isMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              >
                <FontAwesomeIcon icon={isMenuOpen ? faTimes : faBars} />
              </button>

              <nav 
                className={`main-nav ${isMenuOpen ? 'open' : ''}`} 
                ref={menuRef}
              >
                <div className="mobile-menu-header">
                  <Link to="/" onClick={handleLinkClick} className="mobile-logo-link">
                    <img src={logo} alt="ATC" className="mobile-logo" />
                  </Link>
                  <button 
                    className="mobile-close-btn"
                    onClick={closeMenu}
                    aria-label="Fermer le menu"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>

                <ul className="nav-list">
                  {navItems.map((item) => {
                    if (item.dropdown) {
                      return (
                        <li 
                          key={item.id}
                          className={`nav-item dropdown ${activeDropdown === item.id ? 'active' : ''}`}
                          onMouseEnter={() => window.innerWidth > 992 && setActiveDropdown(item.id)}
                          onMouseLeave={() => window.innerWidth > 992 && setActiveDropdown(null)}
                        >
                          <span 
                            className="nav-link dropdown-trigger"
                            onClick={() => {
                              if (window.innerWidth <= 992) {
                                toggleDropdown(item.id);
                              }
                            }}
                          >
                            {item.label}
                            <FontAwesomeIcon icon={faChevronDown} className="dropdown-arrow" />
                          </span>
                          <ul className="dropdown-menu">
                            {item.dropdown.map((sub, subIndex) => {
                              if (sub.subDropdown) {
                                return (
                                  <li key={subIndex} className="has-submenu">
                                    <span 
                                      className="submenu-trigger"
                                      onClick={() => {
                                        if (window.innerWidth <= 992) {
                                          const submenu = document.getElementById(`submenu-${subIndex}`);
                                          if (submenu) {
                                            submenu.classList.toggle('open');
                                          }
                                        }
                                      }}
                                    >
                                      {sub.label}
                                      <FontAwesomeIcon icon={faChevronDown} className="sub-arrow" />
                                    </span>
                                    <ul className="submenu" id={`submenu-${subIndex}`}>
                                      {sub.subDropdown.map((subSub, subSubIndex) => (
                                        <li key={subSubIndex}>
                                          <Link to={subSub.path} onClick={handleLinkClick}>
                                            {subSub.label}
                                          </Link>
                                        </li>
                                      ))}
                                    </ul>
                                  </li>
                                );
                              }
                              return (
                                <li key={subIndex}>
                                  <Link to={sub.path} onClick={handleLinkClick}>
                                    {sub.label}
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        </li>
                      );
                    }
                    return (
                      <li key={item.id} className="nav-item">
                        <NavLink 
                          to={item.path} 
                          className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                          onClick={handleLinkClick}
                        >
                          {item.label}
                        </NavLink>
                      </li>
                    );
                  })}
                  <li className="nav-item nav-cta">
                    <Link to="/contact" className="contact-btn" onClick={handleLinkClick}>
                      <span className="btn-text">{t('btn_Nous_Contacter')}</span>
                    </Link>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </header>
      </div>

      {/* Overlay mobile */}
      {isMenuOpen && (
        <div className="mobile-overlay" onClick={closeMenu}></div>
      )}
    </>
  );
};

export default Header;